import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult, Element } from '@/utils/sajuCalculations';
import { ELEMENT_COLORS, ELEMENT_BG_COLORS } from '@/utils/sajuCalculations';
import { calcSingangyak, extractYongshin, type SingangyakLevel } from '@/utils/singangyakCalculations';

interface Props {
  result: SajuResult;
}

const GAUGE_COLOR: Record<SingangyakLevel, string> = {
  extreme_weak:   '#3B82F6',
  weak:           '#60A5FA',
  mild_weak:      '#93C5FD',
  balanced:       '#10B981',
  mild_strong:    '#F59E0B',
  strong:         '#EF4444',
  extreme_strong: '#DC2626',
};

const ELEMENT_FULL: Record<Element, string> = {
  목: '나무(木)', 화: '불(火)', 토: '흙(土)', 금: '쇠(金)', 수: '물(水)',
};

const ELEMENT_FULL_EN: Record<Element, string> = {
  목: 'Wood (木)', 화: 'Fire (火)', 토: 'Earth (土)', 금: 'Metal (金)', 수: 'Water (水)',
};

export default function SingangyakSection({ result }: Props) {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [gaugeWidth, setGaugeWidth] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const score = calcSingangyak(result);
  const yongshin = extractYongshin(result, score);
  const isEn = i18n.language === 'en';

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const t2 = setTimeout(() => setGaugeWidth(score.total), 200);
    return () => clearTimeout(t2);
  }, [visible, score.total]);

  const gaugeColor = GAUGE_COLOR[score.level];
  const yongshinEl = yongshin.element;

  return (
    <div
      ref={ref}
      className="bg-white border border-gray-100 rounded-2xl p-5 mb-6 transition-all duration-700"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
    >
      {/* 헤더 */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
          <i className="ri-scales-3-line text-amber-500 text-sm" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">{t('singangyak.title')}</h3>
          <p className="text-xs text-gray-400">{t('singangyak.subtitle')}</p>
        </div>
      </div>

      {/* 신강/신약 레벨 카드 */}
      <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: gaugeColor + '15' }}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white mb-2 inline-block" style={{ backgroundColor: gaugeColor }}>
              {isEn ? score.labelEn : score.label}
            </span>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="text-xl font-black" style={{ color: gaugeColor }}>
                {score.emoji} {score.total}
              </span>
              <span className="text-xs font-medium text-gray-400">{t('sewoon.score')}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400 mb-1">{t('singangyak.deonnyeong')}</p>
            <p className="text-sm font-bold" style={{ color: score.deotnyeong ? '#10B981' : '#9CA3AF' }}>
              {score.deotnyeong ? t('singangyak.present') : t('singangyak.absent')}
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed">
          {isEn ? score.descriptionEn : score.description}
        </p>
      </div>

      {/* 게이지 바 */}
      <div className="mb-5">
        <div className="flex justify-between text-xs text-gray-400 mb-1.5">
          <span>{t('singangyak.gauge.extremeWeak')}</span>
          <span>{t('singangyak.gauge.balanced')}</span>
          <span>{t('singangyak.gauge.extremeStrong')}</span>
        </div>
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden relative">
          <div className="absolute top-0 h-full bg-green-100 opacity-50" style={{ left: '45%', width: '10%' }} />
          <div className="h-full rounded-full transition-all duration-1200 ease-out" style={{ width: `${gaugeWidth}%`, backgroundColor: gaugeColor }} />
          <div className="absolute top-0 h-full w-0.5 bg-green-400 opacity-70" style={{ left: '50%' }} />
        </div>
        <div className="flex justify-between text-xs mt-1.5">
          <div className="flex gap-1 items-center"><span style={{ color: '#3B82F6' }}>●</span><span className="text-gray-400">{t('singangyak.gauge.weakSide')}</span></div>
          <div className="flex gap-1 items-center"><span className="text-gray-400">{t('singangyak.gauge.balanced')}</span><span style={{ color: '#10B981' }}>●</span></div>
          <div className="flex gap-1 items-center"><span className="text-gray-400">{t('singangyak.gauge.strongSide')}</span><span style={{ color: '#EF4444' }}>●</span></div>
        </div>
      </div>

      {/* 득령/득지/득세 세부 점수 */}
      <button onClick={() => setShowDetail(d => !d)} className="w-full bg-gray-50 rounded-xl p-3 mb-5 cursor-pointer hover:bg-gray-100 transition-all text-left">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold text-gray-700">{t('singangyak.detail.title')}</p>
          {showDetail ? <i className="ri-arrow-up-s-line text-gray-400" /> : <i className="ri-arrow-down-s-line text-gray-400" />}
        </div>
        {showDetail && (
          <div className="mt-3 space-y-2">
            {[
              { label: t('singangyak.detail.deotnyeong'), desc: t('singangyak.detail.deotnyeong.desc'), value: score.deotnyeong ? 30 : 0, max: 30 },
              { label: t('singangyak.detail.deotji'), desc: t('singangyak.detail.deotji.desc'), value: score.deotji ? 10 : 0, max: 10 },
              { label: t('singangyak.detail.deotse'), desc: t('singangyak.detail.deotse.desc'), value: score.deotseScore, max: 40 },
            ].map(item => (
              <div key={item.label}>
                <div className="flex justify-between mb-1">
                  <div>
                    <span className="text-xs font-semibold text-gray-700">{item.label}</span>
                    <span className="text-xs text-gray-400 ml-1">({item.desc})</span>
                  </div>
                  <span className="text-xs font-bold text-gray-800">{item.value}/{item.max}{t('sewoon.score')}</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(item.value / item.max) * 100}%`, backgroundColor: gaugeColor }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </button>

      {/* 용신 카드 */}
      <div className="rounded-2xl p-4 border-2" style={{ borderColor: ELEMENT_COLORS[yongshinEl], backgroundColor: ELEMENT_BG_COLORS[yongshinEl] }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ backgroundColor: ELEMENT_COLORS[yongshinEl] }}>
            <i className="ri-key-2-line text-white text-sm" />
          </div>
          <div>
            <span className="text-xs text-gray-500">{t('singangyak.yongshin.title')}</span>
            <p className="text-sm font-black" style={{ color: ELEMENT_COLORS[yongshinEl] }}>
              {t('singangyak.yongshin.label')}{isEn ? ELEMENT_FULL_EN[yongshinEl] : ELEMENT_FULL[yongshinEl]}
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed mb-4">{yongshin.tossMessage}</p>
        <p className="text-xs text-gray-500 mb-3">{yongshin.reason}</p>
        <div className="space-y-2">
          {yongshin.items.map(item => (
            <div key={item.category} className="bg-white/60 rounded-xl p-2.5">
              <p className="text-xs font-bold mb-1" style={{ color: ELEMENT_COLORS[yongshinEl] }}>{item.category}</p>
              <div className="flex flex-wrap gap-1.5">
                {item.examples.map(ex => (
                  <span key={ex} className="text-xs px-2 py-0.5 rounded-full bg-white font-medium text-gray-700">{ex}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 주의사항 */}
      <div className="mt-4 bg-gray-50 rounded-xl p-3">
        <p className="text-xs text-gray-400 leading-relaxed">{t('singangyak.note')}</p>
      </div>
    </div>
  );
}
